/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Crown, Fish, MapPin, Gift } from 'lucide-react';
import CatchImage from './CatchImage';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function CatchOfTheMonth() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/catch-of-the-month`)
      .then((r) => setData(r.data))
      .catch((e) => {
        console.error('CatchOfTheMonth fetch failed', e);
        setError(true);
      });
  }, []);

  if (error) return null;
  if (!data) {
    return (
      <div className="glass-card p-6 mb-8 animate-pulse" data-testid="cotm-skeleton">
        <div className="h-5 w-40 bg-[#1f221f] rounded mb-3" />
        <div className="h-7 w-56 bg-[#1f221f] rounded mb-4" />
        <div className="h-24 bg-[#141714] rounded" />
      </div>
    );
  }
  const { year, month, catch: winner, runners_up, prize_description } = data;
  const monthName = MONTHS[(month || 1) - 1];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card p-6 mb-8 overflow-hidden relative"
      data-testid="cotm-section"
    >
      <div className="absolute -top-12 -right-12 w-40 h-40 bg-[#c8b273]/10 rounded-full blur-3xl pointer-events-none" />
      <div className="flex items-center gap-3 mb-4">
        <Crown className="w-6 h-6 text-[#c8b273]" />
        <div>
          <p className="text-xs uppercase tracking-wider text-[#c8b273] font-bold">Catch of the Month</p>
          <h2 className="font-['Barlow_Condensed'] text-2xl font-extrabold uppercase tracking-wide text-white">
            {monthName} {year}
          </h2>
        </div>
      </div>

      {winner ? (
        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 flex gap-4">
            <CatchImage
              row={winner}
              aspectRatio="auto"
              rounded
              className="w-32 h-32 md:w-40 md:h-40 shrink-0 rounded-lg"
              alt={`${winner.weight}lb ${winner.fish_type}`}
              testId="cotm-image"
            />
            <div className="flex-1 min-w-0">
              <p className="text-[#8a948a] text-sm">Top angler</p>
              <p className="text-white text-xl font-bold truncate" data-testid="cotm-username">{winner.username}</p>
              <p className="font-['Barlow_Condensed'] text-3xl font-extrabold text-[#c8b273] mt-1" data-testid="cotm-weight">
                {winner.weight}lb {winner.fish_type}
              </p>
              <p className="text-[#6b7d6b] text-sm flex items-center gap-1 mt-1">
                <MapPin className="w-3.5 h-3.5" /> {winner.lake_name}
              </p>
              <p className="text-[#22c55e] text-sm font-bold mt-1">+{winner.points} points earned</p>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="p-3 bg-[#c8b273]/10 border border-[#c8b273]/30 rounded-lg">
              <p className="text-xs uppercase tracking-wider text-[#c8b273] font-bold flex items-center gap-1">
                <Gift className="w-3.5 h-3.5" /> Prize
              </p>
              <p className="text-white text-xs mt-1 leading-snug">{prize_description}</p>
            </div>
            {runners_up?.length > 0 && (
              <div className="p-3 bg-[#141714] rounded-lg">
                <p className="text-xs uppercase tracking-wider text-[#8a948a] font-bold mb-2">Runners up</p>
                <ul className="space-y-1">
                  {runners_up.slice(0, 3).map((r) => (
                    <li key={r.id} className="text-xs text-[#e2e4e2] flex justify-between" data-testid={`cotm-runner-${r.id}`}>
                      <span className="truncate mr-2">{r.username}</span>
                      <span className="text-[#c8b273] font-bold whitespace-nowrap">{r.weight}lb</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      ) : (
        <p className="text-[#6b7d6b] text-sm py-6 text-center" data-testid="cotm-empty">
          No catches logged this month yet. Log yours and you could be featured here!
        </p>
      )}
    </motion.div>
  );
}
